import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { Case } from '../../../../shared/api/hscan/case';
import { genderToString } from '../../../../shared/utils';
import {
  MobileTable,
  MobileTableRow,
  MobileTableWrapper,
} from '../../common/components/MobileTable';
import {
  RendererProps,
  TableAndElementInformation,
} from '../../common/components/SortableTable/PresentationalTable';
import {
  renderCell,
  renderHeader,
} from '../../common/components/SortableTable/RenderTable';
import {
  ElementContainer,
  HeaderContainer,
  NameContainer,
  DownloadButtonContainer,
  ImageContainer,
  ImageActionContainer,
} from '../ImageList/MobileImageTable';

import {
  PreviewImageDetailButtons,
  PreviewDownloadCaseButton,
} from './PreviewCaseInfoButtons';
import {
  TableContext,
  TableElementContext,
  TableElementInformationProvider,
} from './PreviewMobileCases';

const ShareToPatientNameContainer = styled(NameContainer)`
  padding: 15px;
`;

export const MobileImageTableElement = (
  props: TableAndElementInformation<Case, 'caseId'>,
) => {
  const { element, table, row } = props;
  const { t: globalT } = useTranslation();

  const headerName = useMemo(
    () => (
      <>
        {renderCell(table, row, 'patientName')} /{' '}
        {globalT(`common.shortGender.${genderToString(element.patientSex)}`)} /{' '}
        {renderCell(table, row, 'birthDate')}
      </>
    ),
    [element.patientSex, globalT, row, table],
  );

  return (
    <>
      <ElementContainer>
        <HeaderContainer>
          <ShareToPatientNameContainer>
            {headerName}
          </ShareToPatientNameContainer>
        </HeaderContainer>
        <ImageContainer>
          {
            renderCell(table, row, 'contentIds', {
              width: '100%',
              height: '236px',
            }) as React.ReactNode
          }
          <ImageActionContainer>
            <PreviewImageDetailButtons caseInfo={element} />
          </ImageActionContainer>
        </ImageContainer>
        <MobileTable>
          <tbody>
            <MobileTableRow
              label={
                renderHeader(table, row, 'institutionName') as React.ReactNode
              }>
              {renderCell(table, row, 'institutionName') as React.ReactNode}
            </MobileTableRow>
            <MobileTableRow
              label={renderHeader(table, row, 'studyDate') as React.ReactNode}>
              {renderCell(table, row, 'studyDate') as React.ReactNode}
            </MobileTableRow>
            <MobileTableRow
              label={renderHeader(table, row, 'modality') as React.ReactNode}>
              {renderCell(table, row, 'modality') as React.ReactNode}
            </MobileTableRow>
            <MobileTableRow
              label={renderHeader(table, row, 'bodyPart') as React.ReactNode}>
              {renderCell(table, row, 'bodyPart') as React.ReactNode}
            </MobileTableRow>
          </tbody>
        </MobileTable>
        <DownloadButtonContainer>
          <PreviewDownloadCaseButton caseInfo={element} />
        </DownloadButtonContainer>
      </ElementContainer>
    </>
  );
};

export const PreviewMobileImageTable = (
  props: RendererProps<Case, 'caseId'>,
) => {
  const { Element = MobileImageTableElement } = props;

  return (
    <MobileTableWrapper
      {...props}
      tableContext={TableContext}
      elementContext={TableElementContext}
      ElementProvider={TableElementInformationProvider}
      Element={Element}
    />
  );
};
